// Frequency-filtered service checklist (Session AFF). A maintenance contract's cadence
// (monthly / bi-monthly / quarterly / six-monthly / annual) decides HOW MANY visits a lift
// gets each year, and each ELE-MS-001 Section 3 periodic task has its own band (Monthly /
// Quarterly / Six-monthly / annual / five-yearly records checks). This module works out which
// banded tasks are actually DUE on a given visit, so the engineer records only what the visit
// calls for and the customer certificate shows exactly that (never a task marked "N/A" that was
// simply not due yet).
//
// Pure (no React), so the band maths, the due-set and the scope wording unit test directly. The
// filter is wired ONLY into the engineer job detail (src/routes/engineer/JobDetail.tsx); the
// office recording surfaces (ServiceRecordPanel, BackdatedCompletionModal) are never
// frequency-filtered -- they record against their own supplied item list.
import type { ChecklistItemDef, ChecklistFrequency } from "./offline/types";
import { SERVICE_AREAS_14 } from "./serviceAreas";

// A band's own rate: how many times a year that task should be carried out. five_yearly is
// treated as annual (rate 1): the two five-yearly items are RECORDS checks (verify the 5-yearly
// examination record) and the platform keeps no per-asset history of them, so per the no-history
// rule they surface whenever the annual records checks do. Mirrors the server's frequency bands.
const BAND_RATE: Record<ChecklistFrequency, number> = {
  monthly: 12,
  quarterly: 4,
  six_monthly: 2,
  annual: 1,
  five_yearly: 1, // rides annual: no per-asset five-yearly history exists, so surface with the annual checks
};

// The visits-per-year for a contract cadence. Mirrors the server VISITS_PER_YEAR
// (server/src/modules/jobsheets/fieldLoop.ts) EXACTLY, including bi_monthly = 6. A cadence with
// no mapping (or a blank) returns null, which makes the filter a no-op (the safe default: show
// every applicable item).
const VISITS_PER_YEAR: Record<string, number> = { monthly: 12, bi_monthly: 6, quarterly: 4, six_monthly: 2, annual: 1 };

export function visitsPerYearForFrequency(frequency: string | null | undefined): number | null {
  if (!frequency) return null;
  return VISITS_PER_YEAR[frequency] ?? null;
}

// A plain-English cadence label from the visits-per-year, for the engineer's scope line. Kept
// deliberately clear on a phone ("Six-monthly", not the customer PDF's "Bi-annually").
const VISITS_LABEL: Record<number, string> = { 12: "Monthly", 6: "Bi-monthly", 4: "Quarterly", 2: "Six-monthly", 1: "Annual" };

export function frequencyLabelForVisits(visitsPerYear: number | null | undefined): string | null {
  if (visitsPerYear == null) return null;
  return VISITS_LABEL[visitsPerYear] ?? null;
}

// Is a band due on visit N of a year with V visits?
//   B >= V: the task cannot be done less often than every visit (e.g. a monthly task on a
//           quarterly contract can only ever be done quarterly), so it is due EVERY visit.
//   B <  V: it is spread across the year -- due on the visits that are a multiple of round(V/B).
//           round() spaces a non-integer ratio conservatively (e.g. V=6 bi-monthly, a quarterly
//           task: round(6/4)=2 -> visits 2, 4, 6). For B=1 the spacing is V, so the single annual
//           check lands on the year's LAST visit; on every cadence in {1,2,4,6,12} the last visit
//           (N=V) is a multiple of round(V/B) for every band, so no band is ever empty on it.
export function bandDueOnVisit(band: ChecklistFrequency, visitsPerYear: number, visitNumber: number): boolean {
  const rate = BAND_RATE[band];
  if (rate >= visitsPerYear) return true;
  const spacing = Math.round(visitsPerYear / rate); // rate < V so V/rate > 1, spacing >= 1 (never 0)
  return spacing > 0 && visitNumber % spacing === 0;
}

// The set of bands due on this visit (unbanded items are always due and are not listed here).
// Order matches BAND_RATE for a stable result.
export function dueBands(visitsPerYear: number, visitNumber: number): ChecklistFrequency[] {
  return (Object.keys(BAND_RATE) as ChecklistFrequency[]).filter((b) => bandDueOnVisit(b, visitsPerYear, visitNumber));
}

// The band's "how often" rate for the area-safety tie-break; an unbanded item is the most
// frequent of all (it is always kept, so it never actually reaches the tie-break).
function bandRateOf(freq: ChecklistFrequency | undefined): number {
  return freq ? BAND_RATE[freq] : Number.POSITIVE_INFINITY;
}

// Filter a (already drive-type-filtered) checklist to the items DUE on this visit. Unbanded items
// are always kept; a banded item is kept only when its band is due (bandDueOnVisit). Identity when
// visitsPerYear or visitNumber is null/undefined (a call-out, an equipment job, a lift with no
// cadence, or an older cached job) -- the safe default shows every item.
//
// Certificate-area guarantee: frequency filtering must NEVER leave one of the fourteen customer
// inspection areas blank. On a hydraulic lift the "Ropes, Suspension and Hydraulic Rams" area is
// made up ENTIRELY of banded checks (the quarterly hose inspection and the five-yearly jack exam),
// so on a monthly or bi-monthly hydraulic contract a plain band filter would empty it on the
// early visits. Where filtering would empty an area that DID have applicable items, its
// most-frequent applicable check is kept on every visit so the certificate area is always
// meaningful (this only ever ADDS a check, never drops a required one). Pure.
export function filterChecklistByFrequency<T extends ChecklistItemDef>(
  items: T[],
  visitsPerYear: number | null | undefined,
  visitNumber: number | null | undefined,
): T[] {
  if (visitsPerYear == null || visitNumber == null) return items;
  const keptIds = new Set<string>();
  for (const i of items) {
    if (!i.frequency || bandDueOnVisit(i.frequency, visitsPerYear, visitNumber)) keptIds.add(i.id);
  }
  // Area-safety add-back: never empty a certificate inspection area that had applicable items.
  const byId = new Map(items.map((i) => [i.id, i] as const));
  for (const area of SERVICE_AREAS_14) {
    const present = area.ids.map((id) => byId.get(id)).filter((i): i is T => !!i);
    if (present.length === 0) continue; // no item in this area applies to this lift at all
    if (present.some((i) => keptIds.has(i.id))) continue; // the area already keeps a due item
    let best = present[0]!;
    for (const i of present) if (bandRateOf(i.frequency) > bandRateOf(best.frequency)) best = i;
    keptIds.add(best.id);
  }
  return items.filter((i) => keptIds.has(i.id));
}

// The scope line shown above the checklist, e.g. "Quarterly service: 24 items due this visit".
// Plain punctuation, no double hyphen.
export function checklistScopeLine(frequencyLabel: string, dueCount: number): string {
  return `${frequencyLabel} service: ${dueCount} item${dueCount === 1 ? "" : "s"} due this visit`;
}

// The suffix appended to the scope line when a heavier visit falls due: the annual-inspection
// visit reads "(full annual inspection)"; a six-monthly cadence's every visit reads "(includes
// 6-monthly checks)". The annual note wins when both apply. "" when neither. Plain punctuation.
export function scopeLineSuffix(visitsPerYear: number, visitNumber: number): string {
  if (bandDueOnVisit("annual", visitsPerYear, visitNumber)) return " (full annual inspection)";
  if (visitsPerYear === 2 && bandDueOnVisit("six_monthly", visitsPerYear, visitNumber)) return " (includes 6-monthly checks)";
  return "";
}
